import React, { useState } from 'react';
import { AppNotification, GlobalUser } from '../../types';

export const NotificationMenu = ({ notifications, setNotifications, currentUser, onClose }: { notifications: AppNotification[], setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>, currentUser?: GlobalUser, onClose?: () => void }) => {
    const unreadNotifications = notifications.filter(n => (!n.userId || n.userId === currentUser?.id || n.userId === 'all') && !n.isRead);
    const readNotifications = notifications.filter(n => (!n.userId || n.userId === currentUser?.id || n.userId === 'all') && n.isRead).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
    
    const markAsRead = (id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => (!n.userId || n.userId === currentUser?.id || n.userId === 'all') ? { ...n, isRead: true } : n));
    };

    const clearAll = () => {
        // Keeps unread, deletes read ones
        setNotifications(prev => prev.filter(n => !((!n.userId || n.userId === currentUser?.id || n.userId === 'all') && n.isRead)));
        if (onClose) onClose();
    };

    return (
        <div className="w-full md:w-96 bg-white dark:bg-[#1f2937] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1a2634]">
                <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 text-sm">
                    <span className="material-symbols-outlined text-[18px]">notifications</span>
                    Notificaciones
                </h3>
                {unreadNotifications.length > 0 && (
                    <button onClick={markAllAsRead} className="text-[10px] uppercase font-bold text-primary hover:text-blue-700 transition-colors">Marcar todo como leído</button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto">
                {unreadNotifications.length === 0 && readNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 opacity-50 px-6 text-center">
                        <span className="material-symbols-outlined text-[40px] mb-2 text-slate-400">notifications_off</span>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">No tienes notificaciones pendientes.</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {unreadNotifications.map(n => (
                            <li key={n.id} className="p-4 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors relative group border-t-0">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r"></div>
                                <div className="flex justify-between items-start gap-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[13px] font-bold text-slate-800 dark:text-white leading-tight">{n.title}</p>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{n.description}</p>
                                        <span className="text-[9px] text-slate-400 font-medium uppercase mt-2 block">{new Date(n.date).toLocaleString('es-CL')}</span>
                                    </div>
                                    <button onClick={() => markAsRead(n.id)} className="shrink-0 p-1.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 transition-colors cursor-pointer" title="Marcar como leída">
                                        <span className="material-symbols-outlined text-[14px]">done</span>
                                    </button>
                                </div>
                            </li>
                        ))}
                        {readNotifications.map(n => (
                            <li key={n.id} className="p-4 bg-slate-50/50 dark:bg-slate-800/20 opacity-75">
                                <p className="text-[12px] font-semibold text-slate-500 dark:text-slate-400 leading-tight">{n.title}</p>
                                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 line-clamp-1">{n.description}</p>
                                <span className="text-[9px] text-slate-400/80 font-medium uppercase mt-1.5 block">{new Date(n.date).toLocaleString('es-CL')}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {(readNotifications.length > 0 || unreadNotifications.length > 0) && (
                <div className="p-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-[#1a2634] text-center">
                    <button onClick={clearAll} className="text-xs font-medium text-slate-500 hover:text-red-500 transition-colors cursor-pointer">Limpiar bandeja antigua</button>
                </div>
            )}
        </div>
    );
};

export const NotificationBell = ({ notifications, setNotifications, currentUser }: { notifications: AppNotification[], setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>, currentUser?: GlobalUser }) => {
    const [isOpen, setIsOpen] = useState(false);
    const unreadCount = notifications.filter(n => (!n.userId || n.userId === currentUser?.id || n.userId === 'all') && !n.isRead).length;

    return (
        <div className="relative">
            <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-center rounded-full p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 transition-colors relative cursor-pointer">
                <span className="material-symbols-outlined text-[24px] cursor-pointer">notifications</span>
                {unreadCount > 0 && (
                    <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-white dark:border-[#1a2634]"></span>
                )}
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40 bg-black/20 md:bg-transparent" onClick={() => setIsOpen(false)}></div>
                    <div className="fixed left-4 right-4 top-16 md:absolute md:left-auto md:right-0 md:top-full md:mt-2 z-50 origin-top-right">
                        <NotificationMenu notifications={notifications} setNotifications={setNotifications} currentUser={currentUser} onClose={() => setIsOpen(false)} />
                    </div>
                </>
            )}
        </div>
    );
};
